import { owaComputedFn } from 'owa-computed-fn';
import { getComposeHostItemIndex } from 'owa-addins-store/lib/utils/hostItemIndexUtils';
import getAddinCollection from 'owa-addins-surface-actions/lib/utils/getAddinCollection';
import {
    storePollFilter,
    invalidAddInsFilter,
} from 'owa-addins-surface-actions/lib/utils/getAddinFilters';
import { ExtensibilityModeEnum } from 'owa-addins-types';
import { getDefaultRibbonStyles } from 'owa-command-ribbon-styles/lib/util/getDefaultRibbonStyles';
import { createPinnedAddInButton } from 'owa-mail-compose-controls/lib/components/pinnedAddIns';
import {
    retrieveCommonInfoForRibbon,
    retrieveWindowForRibbon,
} from 'owa-mail-compose-controls/lib/utils/retrieveEditingInfoForRibbon';
import getServerOptionsForFeature from 'owa-outlook-service-option-store/lib/selectors/getOptionsForFeature';
import { OwsOptionsFeatureType } from 'owa-outlook-service-option-store/lib/store/schema/OwsOptionsFeatureType';
import type { AddinCommandSurfaceItem } from 'owa-addins-types';
import type { SurfaceActionsOptions } from 'owa-outlook-service-option-store';
import type { ComposeViewState, SharedFolderComposeViewState } from 'owa-mail-compose-store';
import type { RuntimeControlId } from 'owa-ribbon-ids';
import type { RibbonRuntimeControlsGroup, RibbonRuntimeControl } from './getRuntimeControls';
import { isSharedComposeItemCheckForAddins } from 'owa-mail-store/lib/utils/sharedFolderUtilsForAddins';
import createMessageExtensionCommandSurfaceItem from 'owa-addins-view/lib/utils/createMessageExtensionCommandSurfaceItem';
import { readM365AcquisitionsFromCache } from 'owa-m365-acquisitions/lib/readM365AcquisitionsFromCache';
import { hasComposeMessageExtension } from 'owa-m365-acquisitions/lib/utils/hasMessageExtension';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import { isAddinMultiAccountEnabled, isGlobalFeatureEnabled } from 'owa-feature-flags';
import { isMOS3AppServiceAppsInMailAvailable } from 'owa-m365-acquisitions/lib/utils/isMOS3AppServiceAppsInMailAvailable';
import { logUsage, PerformanceDatapoint } from 'owa-analytics';
import { getExtensionId } from 'owa-m365-acquisitions/lib/utils/getExtensionId';
import {
    hasExchangeAddInComposeCommand,
    hasExchangeAddInReadCommand,
    hasExchangeAddInMultiSelectCommand,
    hasExchangeAddinNoContextCommand,
} from 'owa-m365-acquisitions/lib/utils/hasExchangeAddIn';
import {
    hasExtensionAddInComposeCommand,
    hasExtensionAddInReadCommand,
    hasExtensionAddInMultiSelectCommand,
    hasExtensionAddinNoContextCommand,
} from 'owa-m365-acquisitions/lib/utils/hasExtensionAddIn';

import type { RuntimeControlRibbonCapabilites } from 'owa-m365-acquisitions/lib/utils/runtimeControlRibbonCapabilites';
import { acquisitionsAllowedByPrivacy } from 'owa-m365-acquisitions';
import { isExtensionEnabledForMailboxType } from 'owa-addins-store/lib/utils/isExtensionEnabledForMailboxType';
import getCompliantAppId, {
    getCompliantAppName,
} from 'owa-addins-store/lib/utils/getCompliantAppId';
import getExtensibilityState from 'owa-addins-store/lib/store/getExtensibilityState';
import isExtensibilityContextInitialized from 'owa-addins-store/lib/store/isExtensibilityContextInitialized';
import { getAddinGroupMetadataFromAddin } from 'owa-addins-store/lib/utils/getAddinGroupMetadataFromAddin';
import { lazyInitializeExtensibilityContext } from 'owa-addins-boot/lib/lazyFunctions';
import isSharedMailboxExplicitLogon from 'owa-mail-store/lib/utils/isSharedMailboxExplicitLogon';
import {
    isMultipleSelection,
    isNoItemContext,
} from 'owa-message-extension-flyout/lib/utils/messageExtensionItemContext';
import { getAddinSurfaceFlag } from 'owa-addins-store/lib/selectors/getAddinSurface';
import { lazyUpdatedAddinSurfaceCache } from 'owa-addins-view';
import { getReadRibbonRuntimeControlProps } from '../components/sharedControls/runtimeControlDefinitions';
import type { Addin } from 'owa-addins-store';
import { populateAddinsCapability } from 'owa-capabilities-definitions/lib/populateAddinsCapability';
import { isCapabilityEnabled } from 'owa-capabilities';

const getComposeRibbonPinnedApps = owaComputedFn(function getRibbonPinnedAddIns(
    viewState: ComposeViewState,
    targetWindow: Window
): AddinCommandSurfaceItem[] {
    const mailboxInfo = viewState.mailboxInfo;
    let pinnedApps: AddinCommandSurfaceItem[] = [];
    const duplicatedKeys: string[] = [];

    const pinnedAddInsIds: string[] = getServerOptionsForFeature<SurfaceActionsOptions>(
        OwsOptionsFeatureType.SurfaceActions,
        viewState.mailboxInfo
    ).composeSurfaceAddins;

    const isSharedItem = isSharedComposeItemCheckForAddins(
        (viewState as SharedFolderComposeViewState)?.isInSharedFolder,
        viewState.mailboxInfo
    );
    // if MOS3 is enabled but unavailable, we need addins instead of acquisitions
    // (and readM365AcquisitionsFromCache should return nothing below)
    if (!isMOS3AppServiceAppsInMailAvailable(mailboxInfo)) {
        const hostItemIndex = getComposeHostItemIndex(viewState.composeId);

        pinnedApps = getAddinCollection(
            ExtensibilityModeEnum.MessageCompose,
            isSharedItem,
            hostItemIndex,
            viewState.mailboxInfo,
            targetWindow
        ).filter(
            item =>
                invalidAddInsFilter(item) &&
                storePollFilter(item, viewState.mailboxInfo) &&
                pinnedAddInsIds.includes(item.key)
        );
    }

    // this function is gated by isMessageExtensionsFlyoutEnabled
    const acquisitions = readM365AcquisitionsFromCache(
        undefined /* datapoint */,
        isAddinMultiAccountEnabled() ? mailboxInfo : undefined
    );

    for (const acquisition of acquisitions) {
        const extensionId = getExtensionId(acquisition);
        if (
            (hasComposeMessageExtension(acquisition) ||
                hasExchangeAddInComposeCommand(acquisition) ||
                hasExtensionAddInComposeCommand(acquisition)) &&
            pinnedAddInsIds.some(surfaceId => surfaceId.toLowerCase() === extensionId) &&
            isExtensionEnabledForMailboxType(acquisition, mailboxInfo) &&
            acquisitionsAllowedByPrivacy(acquisition, mailboxInfo)
        ) {
            // We only add the add-in if it doesn't have any duplicate in the list
            const existingAddin = pinnedApps.find(addin => addin.key.toLowerCase() === extensionId);
            if (!existingAddin) {
                let matchingAddin: Addin | undefined;
                let addInGroupLabel: string | undefined;

                if (
                    isGlobalFeatureEnabled('addin-ribbonDesignPhase1') &&
                    isExtensibilityContextInitialized(mailboxInfo)
                ) {
                    ({ matchingAddin, addInGroupLabel } = getAddinMetadataAndTelemetry({
                        mailboxInfo,
                        extensionId,
                        mode: 'MessageCompose',
                        isSharedItem,
                    }));
                }

                pinnedApps.push(
                    createMessageExtensionCommandSurfaceItem({
                        id: extensionId,
                        name: isGlobalFeatureEnabled('addin-ribbonDesignPhase1')
                            ? addInGroupLabel || acquisition.titleDefinition.name || ''
                            : acquisition.titleDefinition.name ?? '',
                        description: acquisition.titleDefinition.shortDescription ?? '',
                        iconUrl: isGlobalFeatureEnabled('addin-ribbonDesignPhase1')
                            ? matchingAddin?.IconUrl ||
                              acquisition.titleDefinition.iconLarge?.uri ||
                              acquisition.titleDefinition.iconSmall?.uri ||
                              ''
                            : acquisition.titleDefinition.iconLarge?.uri ??
                              acquisition.titleDefinition.iconSmall?.uri ??
                              '',
                    })
                );
            } else {
                duplicatedKeys.push(existingAddin.key);
            }
        }
    }

    if (duplicatedKeys.length > 0) {
        logUsage('Duplicate_AddIn_Found_RibbonPinnedList', mailboxInfo, {
            duplicatedKeys: duplicatedKeys.join(','),
        });
    }

    return pinnedApps;
});

let previousPinnedApps:
    | {
          addIn: AddinCommandSurfaceItem;
          ribbonContext: RuntimeControlRibbonCapabilites;
      }[]
    | null = null;
let previousKeys: Set<string> | null = null;
let isPreviousMultiSelect: boolean = false;
const getReadRibbonPinnedApps = owaComputedFn(function getReadRibbonPinnedAddIns(
    targetWindow: Window | undefined,
    isPopout: boolean | undefined
): {
    addIn: AddinCommandSurfaceItem;
    ribbonContext: RuntimeControlRibbonCapabilites;
}[] {
    const pinnedApps: {
        addIn: AddinCommandSurfaceItem;
        ribbonContext: RuntimeControlRibbonCapabilites;
    }[] = [];
    const currentKeys = new Set<string>();

    const mailboxInfo = getModuleContextMailboxInfo();

    let multiSelectChangedState = false;
    let isMultiSelect = false;

    const pinnedAddInsIds: string[] = getServerOptionsForFeature<SurfaceActionsOptions>(
        OwsOptionsFeatureType.SurfaceActions,
        mailboxInfo
    ).readSurfaceAddins;

    // Only readM365AcquisitionsFromCache if we have access to the pinnedAddInsIds
    if (pinnedAddInsIds.length != 0) {
        const acquisitions = readM365AcquisitionsFromCache(
            undefined /* datapoint */,
            isAddinMultiAccountEnabled() ? mailboxInfo : undefined
        );

        for (const acquisition of acquisitions) {
            const extensionId = getExtensionId(acquisition);
            if (
                (hasExtensionAddInReadCommand(acquisition) ||
                    hasExchangeAddInReadCommand(acquisition)) &&
                pinnedAddInsIds.some(surfaceId => surfaceId.toLowerCase() === extensionId) &&
                isExtensionEnabledForMailboxType(acquisition, mailboxInfo) &&
                acquisitionsAllowedByPrivacy(acquisition, mailboxInfo)
            ) {
                let matchingAddin: Addin | undefined = undefined;
                let addInGroupLabel: string | undefined = undefined;
                if (isGlobalFeatureEnabled('addin-ribbonDesignPhase1') && targetWindow) {
                    if (!isExtensibilityContextInitialized(mailboxInfo)) {
                        // Ensure that we wait for the extensibility context to be initialized before proceeding
                        lazyInitializeExtensibilityContext.importAndExecute(mailboxInfo);
                        return [];
                    }
                    isMultiSelect = isPopout !== undefined && isMultipleSelection(isPopout);
                    multiSelectChangedState = isMultiSelect !== isPreviousMultiSelect;
                    const isSharedItem =
                        isMultiSelect || isNoItemContext()
                            ? false
                            : mailboxInfo.type == 'SharedMailbox' ||
                              isSharedMailboxExplicitLogon(mailboxInfo);
                    ({ matchingAddin, addInGroupLabel } = getAddinMetadataAndTelemetry({
                        mailboxInfo,
                        extensionId,
                        mode: 'MessageRead',
                        isSharedItem,
                        isMultiSelect,
                        multiSelectChangedState,
                    }));
                }
                pinnedApps.push({
                    addIn: createMessageExtensionCommandSurfaceItem({
                        id: extensionId,
                        name: isGlobalFeatureEnabled('addin-ribbonDesignPhase1')
                            ? addInGroupLabel || acquisition.titleDefinition.name || ''
                            : acquisition.titleDefinition.name ?? '',
                        description: acquisition.titleDefinition.shortDescription ?? '',
                        iconUrl: isGlobalFeatureEnabled('addin-ribbonDesignPhase1')
                            ? matchingAddin?.IconUrl ||
                              acquisition.titleDefinition.iconLarge?.uri ||
                              acquisition.titleDefinition.iconSmall?.uri ||
                              ''
                            : acquisition.titleDefinition.iconLarge?.uri ??
                              acquisition.titleDefinition.iconSmall?.uri ??
                              '' /* icon XS size */,
                    }),
                    ribbonContext: {
                        isEnabledInNoContext:
                            hasExchangeAddinNoContextCommand(acquisition) ||
                            hasExtensionAddinNoContextCommand(acquisition),
                        isEnabledInMultiSelect:
                            hasExchangeAddInMultiSelectCommand(acquisition) ||
                            hasExtensionAddInMultiSelectCommand(acquisition),
                    },
                });
                currentKeys.add(extensionId);
            }
        }
    }

    if (
        previousKeys &&
        arePinnedAppsEqual(previousKeys, currentKeys) &&
        previousPinnedApps &&
        !multiSelectChangedState
    ) {
        // If keys are the same, return the cached pinnedApps
        return previousPinnedApps;
    }

    // Update the cache
    previousKeys = currentKeys;
    previousPinnedApps = pinnedApps;
    isPreviousMultiSelect = isMultiSelect;

    return pinnedApps;
});

function arePinnedAppsEqual(setA: Set<string>, setB: Set<string>): boolean {
    if (setA.size !== setB.size) {
        return false;
    }
    for (const item of setA) {
        if (!setB.has(item)) {
            return false;
        }
    }
    return true;
}

export function getPinnedAddInsRuntimeControls(): RibbonRuntimeControlsGroup {
    const ribbonPinnedAddIns = new Map<string, RibbonRuntimeControl>();
    let PINNED_ADDIN_CONTROL_ID: RuntimeControlId.ComposePinnedAddIns = 90000;

    const runtimeControlGroup: RibbonRuntimeControlsGroup = {
        controlsGroupName: 'ComposeAddInsPinned',
        shouldAddScalingSteps: () => {
            return true;
        },
        getComposeControlsProps: (props: { composeViewState?: ComposeViewState }) => {
            const viewState = props.composeViewState;
            if (!viewState) {
                return [];
            }
            const defaultStyles = getDefaultRibbonStyles();
            const targetWindow =
                retrieveCommonInfoForRibbon(viewState.composeId)?.targetWindow ?? window;

            const pinnedAddins = getComposeRibbonPinnedApps(viewState, targetWindow);
            return pinnedAddins.map(addIn => {
                let controlId = ribbonPinnedAddIns.get(addIn.key)?.controlId;
                if (!controlId) {
                    controlId = ++PINNED_ADDIN_CONTROL_ID;
                }

                const runtimeControl: RibbonRuntimeControl = {
                    controlId,
                    buttonProps: createPinnedAddInButton(
                        addIn,
                        viewState.composeId,
                        viewState.editorId,
                        controlId,
                        targetWindow,
                        defaultStyles,
                        viewState.mailboxInfo
                    ),
                };
                ribbonPinnedAddIns.set(addIn.key, runtimeControl);
                return runtimeControl;
            });
        },
        getReadControlsProps() {
            return [];
        },
        getControlIds() {
            return Array.from(ribbonPinnedAddIns.values()).map(({ controlId }) => controlId);
        },
        getNumControls() {
            return ribbonPinnedAddIns.size;
        },
    };
    return runtimeControlGroup;
}

export function getReadPinnedAppsRuntimeControls(): RibbonRuntimeControlsGroup {
    const ribbonPinnedApps = new Map<string, RibbonRuntimeControl>();
    let PINNED_ADDIN_CONTROL_ID: RuntimeControlId.ReadPinnedAddins = 90500;

    const runtimeControlGroup: RibbonRuntimeControlsGroup = {
        controlsGroupName: 'ReadAppsPinned',
        shouldAddScalingSteps: () => {
            return true;
        },
        getComposeControlsProps: () => {
            return [];
        },
        getReadControlsProps: owaComputedFn(props => {
            const defaultStyles = getDefaultRibbonStyles();
            const targetWindow = retrieveWindowForRibbon(props.projectionTabId) ?? window;

            const pinnedApps = getReadRibbonPinnedApps(targetWindow, props.isPopout);
            return pinnedApps.map(({ addIn, ribbonContext }) => {
                let controlId = ribbonPinnedApps.get(addIn.key)?.controlId;
                if (!controlId) {
                    controlId = ++PINNED_ADDIN_CONTROL_ID;
                }

                const runtimeControl: RibbonRuntimeControl = {
                    controlId,
                    buttonProps: getReadRibbonRuntimeControlProps(
                        addIn,
                        controlId,
                        targetWindow,
                        defaultStyles,
                        props,
                        ribbonContext
                    ),
                };
                ribbonPinnedApps.set(addIn.key, runtimeControl);
                return runtimeControl;
            });
        }),
        getControlIds() {
            return Array.from(ribbonPinnedApps.values()).map(({ controlId }) => controlId);
        },
        getNumControls() {
            return getReadRibbonPinnedApps(undefined, undefined).length;
        },
    };
    return runtimeControlGroup;
}

/**
 * Helper to get add-in metadata and log telemetry for Compose/Read pinned add-ins.
 *
 * @param params - Object containing:
 *   mailboxInfo: Mailbox info object for the current context
 *   extensionId: The add-in extension ID
 *   mode: 'MessageCompose' or 'MessageRead' to select the extensibility mode
 *   isSharedItem: Whether the item is a shared mailbox or shared context
 *   isMultiSelect: (optional) Whether multi-select mode is active (MessageRead only)
 *   multiSelectChangedState: (optional) Whether multi-select state changed (MessageRead only)
 * @returns Object with:
 *   matchingAddin: The matching Addin object (if found)
 *   addInGroupLabel: The group label for the add-in (if available)
 */
function getAddinMetadataAndTelemetry(params: {
    mailboxInfo: any;
    extensionId: string;
    mode: 'MessageCompose' | 'MessageRead';
    isSharedItem: boolean;
    isMultiSelect?: boolean;
    multiSelectChangedState?: boolean;
}): {
    matchingAddin: Addin | undefined;
    addInGroupLabel: string | undefined;
} {
    const { mailboxInfo, extensionId, mode, isSharedItem, isMultiSelect, multiSelectChangedState } =
        params;
    let matchingAddin: Addin | undefined = undefined;
    let addInGroupLabel: string | undefined = undefined;
    const performanceDatapoint = new PerformanceDatapoint('RibbonDesignPhase1', mailboxInfo);
    const extState = getExtensibilityState(mailboxInfo);
    const shouldcreateAddinCommandSurfaceActions =
        (isGlobalFeatureEnabled('addin-OptimizeInitialization') || extState.Context) &&
        extState.EnabledAddinCommands.isInitialized &&
        isCapabilityEnabled(populateAddinsCapability, mailboxInfo);

    if (shouldcreateAddinCommandSurfaceActions) {
        const enabledAddins: Addin[] = extState.EnabledAddinCommands.getExtensionPoint(
            ExtensibilityModeEnum[mode],
            isSharedItem
        );
        if (!getAddinSurfaceFlag(mode) && enabledAddins) {
            lazyUpdatedAddinSurfaceCache.importAndExecute(enabledAddins, mode);
        }
        matchingAddin = enabledAddins?.find(addin => addin.Id === extensionId) as Addin;
    }

    if (mode === 'MessageRead' && multiSelectChangedState && isMultiSelect && matchingAddin) {
        const multiselectAddins = extState.multiSelectEnabledAddinsCommand;
        const matchingMultiselectAddin = multiselectAddins?.find(
            addin => addin.Id === extensionId
        ) as Addin;
        if (!getAddinSurfaceFlag('MultipleSelection') && multiselectAddins) {
            lazyUpdatedAddinSurfaceCache.importAndExecute(multiselectAddins, 'MultipleSelection');
        }
        matchingAddin = matchingMultiselectAddin ?? matchingAddin;
    }

    const metaData = getAddinGroupMetadataFromAddin(matchingAddin);
    addInGroupLabel = metaData?.groupLabel;
    const providerName = metaData?.providerName;
    const typeString = metaData?.typeString;
    const extensionName = metaData?.name;

    let error = '';
    if (!matchingAddin) {
        error = 'no matching addin';
    } else if (!addInGroupLabel) {
        error = 'no group label';
    }

    performanceDatapoint.addCustomData({
        addInDesignPhase1Success: matchingAddin && addInGroupLabel,
        ...(error && { error: error.trim() }),
        scenario: ExtensibilityModeEnum[mode],
        isSharedItem,
        compliantId: getCompliantAppId(extensionId, providerName, typeString),
        compliantName: getCompliantAppName(extensionName, providerName, typeString),
    });
    performanceDatapoint.end();
    return { matchingAddin, addInGroupLabel };
}
